<template>
  <PageHeader :title="resolveEmployeeFullName(employee)" />

  <Container
    v-if="employee?.featuredImage?.[0]?.url"
    class="mb-10 md:mb-15 xl:mb-17.5 2xl:mb-20"
  >
    <div
      class="mx-auto w-55 shadow-[0_5px_5px_rgba(0,0,0,0.25)] sm:w-[290px] md:w-[354px] md:shadow-[0_3.543px_7.086px_rgba(0,0,0,0.25)] lg:w-[451px] lg:shadow-[0_4.525px_9.051px_rgba(0,0,0,0.25)] xl:w-[506px] xl:shadow-[0_5.07px_10.14px_rgba(0,0,0,0.25)] 2xl:w-[586px] 2xl:shadow-[0_6px_6px_rgba(0,0,0,0.25)]"
    >
      <NuxtImg
        :src="employee?.featuredImage?.[0]?.url"
        :alt="employee?.featuredImage?.[0]?.alt_text || 'Profile image'"
        format="webp"
        sizes="xs:220px sm:290px md:354px lg:451px xl:506px xxl:586px"
        class="size-full object-cover"
        fetchpriority="high"
      />
    </div>
  </Container>

  <div
    class="mb-20 w-full px-5 md:mx-auto md:w-182 md:px-0 lg:mb-25 xl:mb-25 xl:w-[893px] xl:max-w-none 2xl:mb-35 2xl:w-263.5"
  >
    <Accordion
      v-if="employee?.perex"
      :title="t('common.bio')"
      :is-embedded="true"
    >
      <Wysiwyg :content="employee?.perex" :is-embedded="true" class="px-0!" />
    </Accordion>

    <Accordion :title="t('common.contact')" :is-last="true" :is-embedded="true">
      <table class="w-full border-collapse text-left text-sm md:text-base">
        <tbody>
          <tr
            v-if="employee?.role"
            class="border-b border-black/10 last:border-none"
          >
            <td class="w-1/3 py-3 pr-4 font-medium text-black/60 md:w-1/4">
              {{ t('common.position') }}
            </td>
            <td class="py-3 text-black">
              {{ employee.role }}
            </td>
          </tr>

          <tr
            v-if="employee?.email"
            class="border-b border-black/10 last:border-none"
          >
            <td class="w-1/3 py-3 pr-4 font-medium text-black/60 md:w-1/4">
              {{ t('common.email') }}
            </td>
            <td class="py-3 text-black">
              <a
                :href="`mailto:${employee.email}`"
                class="underline-offset-4 hover:underline"
              >
                {{ employee.email }}
              </a>
            </td>
          </tr>

          <tr
            v-if="employee?.phone"
            class="border-b border-black/10 last:border-none"
          >
            <td class="w-1/3 py-3 pr-4 font-medium text-black/60 md:w-1/4">
              {{ t('common.phone') }}
            </td>
            <td class="py-3 text-black">
              <a
                :href="`tel:${employee.phone}`"
                class="underline-offset-4 hover:underline"
              >
                {{ employee.phone }}
              </a>
            </td>
          </tr>

          <tr
            v-if="employee?.mobile"
            class="border-b border-black/10 last:border-none"
          >
            <td class="w-1/3 py-3 pr-4 font-medium text-black/60 md:w-1/4">
              Mobil:
            </td>
            <td class="py-3 text-black">
              <a
                :href="`tel:${employee.mobile}`"
                class="underline-offset-4 hover:underline"
              >
                {{ employee.mobile }}
              </a>
            </td>
          </tr>

          <tr
            v-if="employee?.web"
            class="border-b border-black/10 last:border-none"
          >
            <td class="w-1/3 py-3 pr-4 font-medium text-black/60 md:w-1/4">
              Web:
            </td>
            <td class="py-3 text-black">
              <a
                :href="employee.web"
                target="_blank"
                rel="noopener noreferrer"
                class="underline-offset-4 hover:underline"
              >
                {{ employee.web }}
              </a>
            </td>
          </tr>

          <tr
            v-if="employee?.departments?.length"
            class="border-b border-black/10 last:border-none"
          >
            <td class="w-1/3 py-3 pr-4 font-medium text-black/60 md:w-1/4">
              {{ t('common.workplace') }}
            </td>
            <td class="py-3 text-black">
              <span v-for="(dep, idx) in employee.departments" :key="dep.id">
                {{ dep.title
                }}<span v-if="idx < employee.departments.length - 1">, </span>
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </Accordion>
  </div>

  <div>
    <DynamicContentResolver
      v-if="employee?.content_blocks?.length > 0"
      :blocks="employee.content_blocks"
    />
  </div>

  <Container class="lg:px-0!">
    <RelatedPages
      :pages="employee.departments"
      class="employee-related-pages m-0! px-0! md:mx-auto! md:w-[728px]! md:max-w-none! lg:w-182! lg:max-w-none! xl:w-[893px]! 2xl:w-[1054px]!"
      style="margin-bottom: 0 !important"
    />
  </Container>
</template>

<script setup lang="ts">
const { locale, t } = useI18n()
const setI18nParams = useSetI18nParams()
const route = useRoute()
const { throwFetchError, throwNotFound } = useThrowErrors()

const { data, error } = await usePersistedFetch('GetPeopleBySlug', {
  slug: route.params.slug?.at(-1),
})

if (error.value) throwFetchError()

if (!data.value?.getPeopleBySlug) throwNotFound()

const employee = computed(() => data.value?.getPeopleBySlug)

watch(
  employee,
  (val) => {
    if (val) {
      setI18nParams({
        cs: { slug: useGetSlugSegments(val, 'cs') },
        en: { slug: useGetSlugSegments(val, 'en') },
      })
    }
  },
  { immediate: true },
)

const breadcrumbsData = useBreadcrumbs()

useDynamicSeo(employee, {
  bodyAttrs: {
    style: 'background-color: #E4E4E4',
  },
})

onMounted(() => {
  breadcrumbsData.value = employee.value.breadcrumbs[locale.value]
})
</script>
